import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Search, Users, UserPlus, RefreshCw, Eye, Mail, Phone, Building2, MapPin, Calendar, LogIn } from "lucide-react";
import { toast } from "sonner";
import { useUserRole } from "@/hooks/useUserRole";



interface Client {
  id: string;
  email: string;
  created_at: string;
  last_sign_in_at: string | null;
  first_name: string;
  last_name: string;
  phone: string;
  company: string;
  street_address: string;
  city: string;
  province: string;
  postal_code: string;
  country: string;
}

const AdminClients = () => {
  const navigate = useNavigate();
  const { isAdmin } = useUserRole();
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selectedClient, setSelectedClient] = useState<Client | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const emptyForm = {
    email: "", password: "", first_name: "", last_name: "", phone: "",
    company: "", street_address: "", city: "", province: "Québec",
    postal_code: "", country: "Canada",
  };
  const [form, setForm] = useState(emptyForm);
  const [newPassword, setNewPassword] = useState("");
  const [resettingPwd, setResettingPwd] = useState(false);

  const handleResetPassword = async () => {
    if (!selectedClient || newPassword.length < 6) return;
    setResettingPwd(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-reset-password", {
        body: { userId: selectedClient.id, password: newPassword },
      });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      toast.success("Mot de passe temporaire défini. Le client devra le changer à la prochaine connexion.");
      setNewPassword("");
    } catch (err: any) {
      toast.error(err.message || "Erreur lors de la réinitialisation");
    } finally {
      setResettingPwd(false);
    }
  };


  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.email || !form.password) { toast.error("Email et mot de passe requis"); return; }
    if (form.password.length < 6) { toast.error("Mot de passe min. 6 caractères"); return; }
    setCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke("create-user", { body: form });
      if (error) throw new Error(error.message);
      if (data?.error) throw new Error(data.error);
      toast.success("Client créé avec succès !");
      setForm(emptyForm);
      setCreateOpen(false);
      fetchClients();
    } catch (err: any) {
      toast.error(err.message || "Erreur lors de la création");
    } finally {
      setCreating(false);
    }
  };



  const fetchClients = async () => {
    setLoading(true);
    try {
      const { data: sessionData } = await supabase.auth.getSession();
      const token = sessionData.session?.access_token;
      
      const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
      const url = `https://${projectId}.supabase.co/functions/v1/list-clients?search=${encodeURIComponent(search)}`;
      
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || "Erreur");
      }

      setClients(result.clients || []);
    } catch (err: any) {
      toast.error(err.message || "Erreur lors du chargement des clients");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchClients();
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    fetchClients();
  };

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return "Jamais";
    return new Date(dateStr).toLocaleDateString("fr-CA", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateShort = (dateStr: string | null) => {
    if (!dateStr) return "Jamais";
    return new Date(dateStr).toLocaleDateString("fr-CA", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const getFullAddress = (client: Client) => {
    const parts = [
      client.street_address,
      client.city,
      client.province,
      client.postal_code,
      client.country,
    ].filter(Boolean);
    return parts.length > 0 ? parts.join(", ") : "—";
  };

  const DetailRow = ({ icon: Icon, label, value }: { icon: any; label: string; value: string }) => (
    <div className="flex items-start gap-3 py-3 border-b border-border last:border-0">
      <Icon className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium break-all">{value || "—"}</p>
      </div>
    </div>
  );

  return (
    <div className="bg-background">
      <main className="container mx-auto px-4 py-8">

        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6">
          <ArrowLeft className="mr-2 h-4 w-4" />
          Retour
        </Button>

        <Card>
          <CardHeader>
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
              <CardTitle className="flex items-center gap-2">
                <Users className="h-5 w-5" />
                Gestion des clients ({clients.length})
              </CardTitle>
              {isAdmin && (
                <Button onClick={() => setCreateOpen(true)} size="sm">
                  <UserPlus className="mr-2 h-4 w-4" />
                  Nouveau client
                </Button>
              )}


            </div>

            <form onSubmit={handleSearch} className="flex gap-2 mt-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Rechercher par nom, courriel, entreprise, téléphone..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <Button type="submit" variant="secondary" size="icon">
                <Search className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="outline"
                size="icon"
                onClick={() => { setSearch(""); setTimeout(fetchClients, 0); }}
              >
                <RefreshCw className="h-4 w-4" />
              </Button>
            </form>
          </CardHeader>

          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : clients.length === 0 ? (
              <p className="text-center text-muted-foreground py-12">
                Aucun client trouvé
              </p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Nom</TableHead>
                      <TableHead>Courriel</TableHead>
                      <TableHead className="hidden md:table-cell">Entreprise</TableHead>
                      <TableHead className="hidden md:table-cell">Téléphone</TableHead>
                      <TableHead className="hidden lg:table-cell">Ville</TableHead>
                      <TableHead className="hidden lg:table-cell">Inscrit le</TableHead>
                      <TableHead className="text-right">Détails</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clients.map((client) => (
                      <TableRow key={client.id} className="cursor-pointer" onClick={() => setSelectedClient(client)}>
                        <TableCell className="font-medium">
                          {client.first_name || client.last_name
                            ? `${client.first_name} ${client.last_name}`.trim()
                            : "—"}
                        </TableCell>
                        <TableCell className="text-sm">{client.email}</TableCell>
                        <TableCell className="hidden md:table-cell text-sm">
                          {client.company || "—"}
                        </TableCell>
                        <TableCell className="hidden md:table-cell text-sm">
                          {client.phone || "—"}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm">
                          {client.city ? `${client.city}, ${client.province}` : "—"}
                        </TableCell>
                        <TableCell className="hidden lg:table-cell text-sm">
                          {formatDateShort(client.created_at)}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => { e.stopPropagation(); setSelectedClient(client); }}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </main>


      {/* Client Detail Dialog */}
      <Dialog open={!!selectedClient} onOpenChange={(open) => !open && setSelectedClient(null)}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              {selectedClient?.first_name || selectedClient?.last_name
                ? `${selectedClient?.first_name} ${selectedClient?.last_name}`.trim()
                : "Client"}
            </DialogTitle>
          </DialogHeader>

          {selectedClient && (
            <div className="space-y-0">
              <DetailRow icon={Mail} label="Courriel" value={selectedClient.email} />
              <DetailRow icon={Phone} label="Téléphone" value={selectedClient.phone} />
              <DetailRow icon={Building2} label="Entreprise" value={selectedClient.company} />
              <div className="flex items-start gap-3 py-3 border-b border-border">
                <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="min-w-0 w-full space-y-1">
                  <p className="text-xs text-muted-foreground">Adresse</p>
                  <p className="text-sm font-medium">{selectedClient.street_address || "—"}</p>
                  <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                    <div>
                      <p className="text-xs text-muted-foreground">Ville</p>
                      <p className="text-sm font-medium">{selectedClient.city || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Province</p>
                      <p className="text-sm font-medium">{selectedClient.province || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Code postal</p>
                      <p className="text-sm font-medium">{selectedClient.postal_code || "—"}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Pays</p>
                      <p className="text-sm font-medium">{selectedClient.country || "—"}</p>
                    </div>
                  </div>
                </div>
              </div>
              <DetailRow icon={Calendar} label="Date d'inscription" value={formatDate(selectedClient.created_at)} />
              <DetailRow icon={LogIn} label="Dernière connexion" value={formatDate(selectedClient.last_sign_in_at)} />

              {isAdmin && (
                <div className="border-t pt-4 mt-4 space-y-2">
                  <Label htmlFor="new_pwd" className="text-sm font-semibold">
                    Définir un mot de passe temporaire
                  </Label>
                  <p className="text-xs text-muted-foreground">
                    Le client devra le modifier à sa prochaine connexion.
                  </p>
                  <div className="flex gap-2 px-1 py-1">
                    <Input
                      id="new_pwd"
                      type="text"
                      placeholder="Min. 6 caractères"
                      value={newPassword}
                      onChange={(e) => setNewPassword(e.target.value)}
                      minLength={6}
                      className="flex-1 min-w-0"
                    />
                    <Button
                      onClick={handleResetPassword}
                      disabled={resettingPwd || newPassword.length < 6}
                      className="flex-shrink-0"
                    >
                      {resettingPwd ? "..." : "Définir"}
                    </Button>
                  </div>
                </div>
              )}
            </div>

          )}
        </DialogContent>
      </Dialog>

      {/* Create Client Dialog (admin only) */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="w-[95vw] sm:max-w-3xl max-h-[90vh] overflow-y-auto p-6 sm:p-8">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" />
              Créer un nouveau client
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCreate} className="space-y-6 px-1 py-1">
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Identifiants de connexion</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="c_email">Courriel *</Label>
                  <Input id="c_email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="c_password">Mot de passe *</Label>
                  <Input id="c_password" type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} required minLength={6} />
                </div>
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Informations personnelles</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2"><Label htmlFor="c_fn">Prénom</Label><Input id="c_fn" value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} /></div>
                <div className="space-y-2"><Label htmlFor="c_ln">Nom</Label><Input id="c_ln" value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} /></div>
                <div className="space-y-2"><Label htmlFor="c_ph">Téléphone</Label><Input id="c_ph" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div className="space-y-2"><Label htmlFor="c_co">Entreprise</Label><Input id="c_co" value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} /></div>
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Adresse</h3>
              <div className="space-y-2"><Label htmlFor="c_addr">Adresse</Label><Input id="c_addr" value={form.street_address} onChange={(e) => setForm({ ...form, street_address: e.target.value })} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="space-y-2"><Label htmlFor="c_city">Ville</Label><Input id="c_city" value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} /></div>
                <div className="space-y-2"><Label htmlFor="c_prov">Province</Label><Input id="c_prov" value={form.province} onChange={(e) => setForm({ ...form, province: e.target.value })} /></div>
                <div className="space-y-2"><Label htmlFor="c_pc">Code postal</Label><Input id="c_pc" value={form.postal_code} onChange={(e) => setForm({ ...form, postal_code: e.target.value })} /></div>
                <div className="space-y-2"><Label htmlFor="c_cn">Pays</Label><Input id="c_cn" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} /></div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={() => setCreateOpen(false)}>Annuler</Button>
              <Button type="submit" disabled={creating}>{creating ? "Création..." : "Créer le client"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>

  );
};

export default AdminClients;